import { type AiLanguagePracticePage, getAiLanguagePracticePage } from "./aiLanguagePracticePages";

export type PracticeScenarioPreset = {
  id: string;
  languageSlug: string;
  scenarioSlug: string;
  scenarioName: string;
  shortName: string;
  languageName: string;
  languageCode: string;
  level: "beginner" | "intermediate";
  searchIntent: string;
  sessionGoal: string;
  instructions: string;
  situations: string[];
  usefulPhrases: string[];
  commonMistakes: string[];
  practicePrompts: string[];
};

type ScenarioSeed = Omit<
  PracticeScenarioPreset,
  "id" | "languageName" | "languageCode" | "languageSlug"
> & {
  languageSlug: string;
};

const scenarioSeeds: ScenarioSeed[] = [
  {
    languageSlug: "spanish",
    scenarioSlug: "restaurant",
    scenarioName: "Restaurant Conversation",
    shortName: "restaurant practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Spanish restaurant conversation practice, ordering food phrases, or waiter role-play.",
    sessionGoal:
      "Order food, ask simple follow-up questions, and recover politely when the server answers quickly.",
    instructions:
      "Run a Spanish restaurant conversation practice session. Roleplay a server and help the learner order food, ask about ingredients, request the bill, and respond when the server asks follow-up questions. Keep Spanish prompts short, correct one practical phrase at a time, and ask the learner to repeat the improved version aloud.",
    situations: ["Ordering a meal", "Asking about ingredients", "Requesting the bill"],
    usefulPhrases: ["I would like...", "Does this have...?", "Can we have the bill?"],
    commonMistakes: [
      "Using textbook sentences that are too long for a live order",
      "Forgetting polite request phrases",
      "Pausing when the server asks a follow-up question",
    ],
    practicePrompts: [
      "Order a drink and one main dish.",
      "Ask whether the dish has meat or dairy.",
      "Say there is a problem with the order politely.",
    ],
  },
  {
    languageSlug: "spanish",
    scenarioSlug: "travel",
    scenarioName: "Travel Conversation",
    shortName: "travel practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Spanish travel conversation practice, airport phrases, hotel phrases, or directions practice.",
    sessionGoal:
      "Ask for directions, check into a hotel, and handle simple travel problems without switching back to English.",
    instructions:
      "Run a Spanish travel conversation practice session. Roleplay airport, hotel, and street-direction situations. Keep the learner speaking in short Spanish turns, correct only the most useful phrase after each answer, and repeat the corrected version before moving to the next travel problem.",
    situations: ["Hotel check-in", "Asking directions", "Airport help"],
    usefulPhrases: ["Where is...?", "I have a reservation.", "Can you help me?"],
    commonMistakes: [
      "Mixing directions vocabulary under pressure",
      "Using English word order for practical requests",
      "Answering with isolated words instead of short sentences",
    ],
    practicePrompts: [
      "Ask how to get to the train station.",
      "Check into a hotel with a reservation.",
      "Explain that your luggage is missing.",
    ],
  },
  {
    languageSlug: "french",
    scenarioSlug: "cafe",
    scenarioName: "Cafe Conversation",
    shortName: "cafe practice",
    level: "beginner",
    searchIntent:
      "For learners searching for French cafe conversation practice, ordering coffee in French, or pronunciation support.",
    sessionGoal:
      "Order in a cafe, ask for options, and repeat corrected phrases with French rhythm and liaison.",
    instructions:
      "Run a French cafe conversation practice session. Roleplay a cafe worker and help the learner order drinks, ask about options, and pay. Keep the conversation practical, correct liaison, silent endings, and one natural phrase per turn, then have the learner repeat the better sentence aloud.",
    situations: ["Ordering coffee", "Asking about pastries", "Paying politely"],
    usefulPhrases: ["I would like...", "What do you recommend?", "Can I pay by card?"],
    commonMistakes: [
      "Pronouncing silent endings while ordering",
      "Forgetting polite cafe formulas",
      "Losing liaison in common phrases",
    ],
    practicePrompts: [
      "Order a coffee and a pastry.",
      "Ask what the cafe recommends.",
      "Say you want to pay by card.",
    ],
  },
  {
    languageSlug: "french",
    scenarioSlug: "travel",
    scenarioName: "Travel Conversation",
    shortName: "travel practice",
    level: "beginner",
    searchIntent:
      "For learners searching for French travel phrases, hotel conversation practice, or directions in French.",
    sessionGoal:
      "Ask for directions, check into a hotel, and handle simple travel needs with corrected French pronunciation.",
    instructions:
      "Run a French travel conversation practice session. Roleplay hotel, train station, and street-direction situations. Ask one short follow-up at a time, correct practical travel phrasing plus one pronunciation point, and keep the learner repeating improved French sentences aloud.",
    situations: ["Train station help", "Hotel check-in", "Street directions"],
    usefulPhrases: ["Where is...?", "I have a booking.", "I am looking for..."],
    commonMistakes: [
      "Translating English direction phrases too literally",
      "Forgetting gender agreement in simple requests",
      "Letting pronunciation corrections stop the conversation",
    ],
    practicePrompts: [
      "Ask where the nearest metro station is.",
      "Check into a hotel and spell your name.",
      "Explain that you are lost.",
    ],
  },
  {
    languageSlug: "japanese",
    scenarioSlug: "introductions",
    scenarioName: "Self-Introduction Practice",
    shortName: "introduction practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Japanese self introduction practice, jikoshokai examples, or beginner speaking drills.",
    sessionGoal:
      "Give a short polite self-introduction and answer simple follow-up questions without overbuilding the sentence.",
    instructions:
      "Run a beginner Japanese self-introduction practice session. Help the learner say their name, where they are from, what they study or do, and one hobby. Keep the register polite, correct particles and sentence endings, and ask the learner to repeat short natural answers before expanding.",
    situations: ["First meeting", "Class introduction", "Hobby follow-up"],
    usefulPhrases: ["My name is...", "I am from...", "My hobby is..."],
    commonMistakes: [
      "Mixing polite and casual endings",
      "Using English-shaped sentence order",
      "Choosing particles from memory instead of sentence role",
    ],
    practicePrompts: [
      "Introduce your name and where you are from.",
      "Say one hobby in a polite sentence.",
      "Answer a simple follow-up about your work or study.",
    ],
  },
  {
    languageSlug: "japanese",
    scenarioSlug: "restaurant",
    scenarioName: "Restaurant Conversation",
    shortName: "restaurant practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Japanese restaurant conversation practice, food ordering phrases, or polite ordering drills.",
    sessionGoal:
      "Order food politely, ask a simple question, and respond naturally when the staff asks a follow-up.",
    instructions:
      "Run a Japanese restaurant conversation practice session. Roleplay restaurant staff and help the learner order food politely, ask about recommendations, and request the bill. Keep prompts short, correct particles, politeness level, and long vowels, then have the learner repeat the improved sentence aloud.",
    situations: ["Entering a restaurant", "Ordering food", "Paying"],
    usefulPhrases: ["I will have...", "What do you recommend?", "Can I have the bill?"],
    commonMistakes: [
      "Overusing memorized phrases without adapting to follow-ups",
      "Dropping polite endings",
      "Flattening long vowels in food words",
    ],
    practicePrompts: [
      "Ask for a table for two.",
      "Order one dish and one drink.",
      "Ask what the server recommends.",
    ],
  },
  {
    languageSlug: "korean",
    scenarioSlug: "ordering",
    scenarioName: "Ordering Food Practice",
    shortName: "ordering practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Korean ordering food practice, restaurant phrases, or polite Korean role-play.",
    sessionGoal:
      "Order food politely, use natural endings, and keep the exchange going for a few short turns.",
    instructions:
      "Run a Korean food-ordering practice session. Roleplay a restaurant worker and help the learner order, ask about spice level, and pay. Keep the speech level polite, correct sentence endings and batchim pronunciation, and ask the learner to repeat the improved phrase before continuing.",
    situations: ["Ordering a dish", "Asking about spice", "Paying"],
    usefulPhrases: ["Please give me...", "Is it spicy?", "Can I pay by card?"],
    commonMistakes: [
      "Mixing casual and polite endings",
      "Letting batchim endings disappear",
      "Following romanization habits instead of Hangul sound patterns",
    ],
    practicePrompts: [
      "Order one dish politely.",
      "Ask whether the food is spicy.",
      "Say you want to pay by card.",
    ],
  },
  {
    languageSlug: "korean",
    scenarioSlug: "job-interview",
    scenarioName: "Job Interview Practice",
    shortName: "job interview practice",
    level: "intermediate",
    searchIntent:
      "For learners searching for Korean job interview practice, interview answers, or professional speaking help.",
    sessionGoal:
      "Answer basic interview questions with polite, concise Korean and repair awkward translated phrasing.",
    instructions:
      "Run a Korean job interview speaking practice session. Act as a friendly interviewer and ask about the learner's background, strengths, and why they want the role. Keep the register professional and polite, correct translated phrasing, sentence endings, and confidence, then ask for a shorter improved answer.",
    situations: ["Self-introduction", "Strengths and experience", "Why this role"],
    usefulPhrases: ["My strength is...", "I have experience in...", "I would like to..."],
    commonMistakes: [
      "Answering with sentences that sound translated from English",
      "Using casual endings in a professional context",
      "Giving long answers before the core point is clear",
    ],
    practicePrompts: [
      "Introduce yourself for an interview.",
      "Explain one strength with a short example.",
      "Say why you are interested in the role.",
    ],
  },
  {
    languageSlug: "arabic",
    scenarioSlug: "greetings",
    scenarioName: "Greetings Practice",
    shortName: "greetings practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Arabic greetings practice, beginner Arabic conversation, or spoken greeting phrases.",
    sessionGoal:
      "Use greetings, introductions, and simple follow-ups while separating formal phrases from practical spoken use.",
    instructions:
      "Run an Arabic greetings and introduction practice session. Help the learner greet someone, ask how they are, introduce themselves, and answer one simple follow-up. Mark whether phrases are formal or practical spoken Arabic, correct emphatic consonants and ayn or ghayn, and keep each turn short.",
    situations: ["Greeting someone", "Introducing yourself", "Simple follow-up"],
    usefulPhrases: ["Hello", "How are you?", "My name is..."],
    commonMistakes: [
      "Using very formal phrases in casual spoken moments",
      "Flattening emphatic consonants",
      "Reading slowly instead of practicing spoken rhythm",
    ],
    practicePrompts: [
      "Greet someone and ask how they are.",
      "Introduce your name and where you are from.",
      "Ask one simple follow-up question.",
    ],
  },
  {
    languageSlug: "arabic",
    scenarioSlug: "market",
    scenarioName: "Market Conversation",
    shortName: "market practice",
    level: "beginner",
    searchIntent:
      "For learners searching for Arabic market conversation practice, shopping phrases, or price negotiation role-play.",
    sessionGoal:
      "Ask prices, request items, and handle a simple market exchange with clear pronunciation.",
    instructions:
      "Run an Arabic market conversation practice session. Roleplay a market seller and help the learner ask prices, request items, and respond to a simple offer. Keep the exchange practical, correct emphatic consonants and useful spoken phrasing, and repeat one improved sentence after each turn.",
    situations: ["Asking the price", "Requesting an item", "Responding to an offer"],
    usefulPhrases: ["How much is this?", "I want...", "Can you lower the price?"],
    commonMistakes: [
      "Pausing on unfamiliar consonants",
      "Using formal written phrasing for a practical exchange",
      "Forgetting short polite fillers that keep the conversation moving",
    ],
    practicePrompts: [
      "Ask the price of fruit at a market.",
      "Say you want two of an item.",
      "Ask politely if the price can be lower.",
    ],
  },
];

function toPreset(seed: ScenarioSeed): PracticeScenarioPreset {
  const language = getAiLanguagePracticePage(seed.languageSlug);

  if (!language) {
    throw new Error(`Missing AI language practice page for scenario seed: ${seed.languageSlug}`);
  }

  return {
    ...seed,
    id: `${language.slug}-${seed.scenarioSlug}`,
    languageSlug: language.slug,
    languageName: language.name,
    languageCode: language.code,
  };
}

export const practiceScenarioPresets: PracticeScenarioPreset[] = scenarioSeeds.map(toPreset);

export function getPracticeScenarioPath(preset: PracticeScenarioPreset): string {
  return `/practice/${preset.languageSlug}/${preset.scenarioSlug}`;
}

export function getPracticeScenarioStartPath(preset: PracticeScenarioPreset): string {
  const params = new URLSearchParams({
    languageCode: preset.languageCode,
    languageLevel: preset.level,
    sessionMode: "conversation",
    scenario: preset.id,
  });

  return `/?${params.toString()}`;
}

export function getPracticeScenarioPreset(
  languageSlug: string,
  scenarioSlug: string
): PracticeScenarioPreset | undefined {
  return practiceScenarioPresets.find(
    (preset) => preset.languageSlug === languageSlug && preset.scenarioSlug === scenarioSlug
  );
}

export function getPracticeScenarioPresetById(
  id: string | null | undefined
): PracticeScenarioPreset | undefined {
  if (!id) return undefined;
  return practiceScenarioPresets.find((preset) => preset.id === id);
}

export function getPracticeScenarioLanguagePage(
  preset: PracticeScenarioPreset
): AiLanguagePracticePage {
  const page = getAiLanguagePracticePage(preset.languageSlug);
  if (!page) {
    throw new Error(`Missing AI language practice page for scenario preset: ${preset.id}`);
  }
  return page;
}

export function getPracticeScenarioStaticParams(): Array<{
  language: string;
  scenario: string;
}> {
  return practiceScenarioPresets.map((preset) => ({
    language: preset.languageSlug,
    scenario: preset.scenarioSlug,
  }));
}

export function getRelatedPracticeScenarioPresets(
  preset: PracticeScenarioPreset
): PracticeScenarioPreset[] {
  const sameLanguage = practiceScenarioPresets.filter(
    (candidate) => candidate.languageSlug === preset.languageSlug && candidate.id !== preset.id
  );

  if (sameLanguage.length >= 2) {
    return sameLanguage.slice(0, 3);
  }

  return [
    ...sameLanguage,
    ...practiceScenarioPresets.filter(
      (candidate) => candidate.languageSlug !== preset.languageSlug
    ),
  ].slice(0, 3);
}
